import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { useNavigate, Link } from 'react-router-dom';
import { Send, AlertCircle, ChevronRight, ArrowLeft } from 'lucide-react';
import toast from 'react-hot-toast';
import { supabase } from '../lib/supabase';
import PageBanner from './PageBanner';

const unitThemes = {
  cs: { name: 'Computer Society', color: '#F6A813', icon: '/asset/logos/cswh.png' },
  ias: { name: 'Industrial Applications Society', color: '#00629B', icon: '/asset/logos/iasjdid.png' },
  'ies-pes': { name: 'Power & Energy Society', color: '#008542', icon: '/asset/logos/iespeswh.png' },
  wie: { name: 'Women in Engineering', color: '#781F72', icon: '/asset/logos/wiejdid.png' },
  sight: { name: 'Humanitarian Technology', color: '#00629B', icon: '/asset/logos/sightwh.png' },
  smc: { name: 'Systems, Man & Cybernetics', color: '#00629B', icon: '/asset/logos/smcwh.png' },
  ras: { name: 'Robotics & Automation', color: '#8B0000', icon: '/asset/logos/raswh.png' },
};

const allUnits = Object.entries(unitThemes).map(([key, value]) => ({ id: key, ...value }));

export default function UnitRegisterPage({ unit }) {
  const navigate = useNavigate();
  const [isSubmitting, setIsSubmitting] = useState(false);
  
  const theme = unitThemes[unit] || { name: unit.toUpperCase(), color: '#006699', icon: '' };
  
  // Custom Icon Component passing the logo
  const CustomIcon = ({ size }) => (
    <img 
      src={theme.icon} 
      alt={theme.name} 
      style={{ width: size, height: size }} 
      className="object-contain brightness-0 invert" 
    />
  );

  const [formData, setFormData] = useState({
    full_name: '',
    email: '',
    phone: '',
    affiliation: '',
    comments: '',
  });

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.full_name || !formData.email || !formData.phone || !formData.affiliation) {
      toast.error('Please fill in all required fields.', {
        style: { borderRadius: '0', background: '#333', color: '#fff' },
      });
      return;
    }

    setIsSubmitting(true);

    try {
      const tableName = `${unit.replace('-', '')}_registrations`;
      const { error } = await supabase.from(tableName).insert([
        {
          full_name: formData.full_name,
          email: formData.email,
          phone: formData.phone,
          affiliation: formData.affiliation,
          comments: formData.comments,
        },
      ]);

      if (error) throw error;

      toast.success('Registration successful!', {
        icon: 'n',
        style: { borderRadius: '0', background: '#006699', color: '#fff', fontWeight: 'bold' },
      });

      navigate('/thank-you');
    } catch (error) {
      console.error('Error submitting form:', error);
      toast.error(error.message || 'Registration failed. Please try again.', {
        style: { borderRadius: '0', background: '#dc2626', color: '#fff' },
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const otherUnits = allUnits.filter(u => u.id !== unit);

  return (
    <div key={unit} className="min-h-screen bg-[#fafcff] relative overflow-hidden">
      <div
        className="absolute inset-0 pointer-events-none"
        style={{
          backgroundImage: `repeating-linear-gradient(-45deg, rgba(15, 105, 157, 0.02) 0px, rgba(15, 105, 157, 0.02) 1px, transparent 1px, transparent 18px)`,
        }}
      />
      
      <PageBanner
        title={`Join ${theme.name}`}
        description={`Become a part of the IEEE ${theme.name} chapter and expand your potential.`}
        Icon={CustomIcon}
      />
      
      <main className="relative z-10 max-w-6xl mx-auto px-6 py-16 lg:py-24">
        <div className="flex flex-col lg:flex-row gap-8">
          
          {/* Main Form Area */}
          <div className="w-full lg:w-3/4">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              className="bg-white border border-[#006699]/20 shadow-2xl relative rounded-none"
            >
              {/* L Corners */}
              <div className="absolute top-0 left-0 w-12 h-12 border-t-4 border-l-4 border-[#006699] pointer-events-none z-10" />
              <div className="absolute bottom-0 right-0 w-12 h-12 border-b-4 border-r-4 border-[#006699] pointer-events-none z-10" />
              
              {/* Banner Image inside box like JoinPage */}
              <div className="w-full h-48 sm:h-56 lg:h-72 relative overflow-hidden flex flex-col items-center justify-center">
                <img 
                  src="/asset/event/anniv6.webp" 
                  alt="IEEE Anniversary" 
                  className="absolute inset-0 w-full h-full object-cover blur-[2px] scale-105" 
                />
                
                {/* Overlay */}
                <div className="absolute inset-0 bg-black/40" />
                
                <motion.div
                  initial={{ opacity: 0, y: 15 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.3, duration: 0.8 }}
                  className="relative z-10 flex items-center justify-center h-full w-full py-8"
                >
                  {/* Big Centered Logo */}
                  <motion.div 
                    initial={{ scale: 0.8, opacity: 0 }}
                    animate={{ scale: 1, opacity: 1 }}
                    transition={{ delay: 0.5, type: 'spring', stiffness: 200 }}
                  >
                    <img 
                      src={theme.icon} 
                      alt={theme.name} 
                      className="w-40 h-40 sm:w-56 sm:h-56 md:w-64 md:h-64 lg:w-80 lg:h-80 object-contain brightness-0 invert drop-shadow-2xl" 
                    />
                  </motion.div>
                </motion.div>
              </div>

              <div className="p-8 sm:p-12 lg:p-16 pt-10">
                <form onSubmit={handleSubmit} className="space-y-8">
                  {/* Grid for basic info */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                    <div className="space-y-2">
                      <label className="text-xs font-bold text-[#00334d] uppercase tracking-widest flex items-center gap-2">
                        Full Name <span className="text-red-500">*</span>
                      </label>
                      <input type="text" name="full_name" required value={formData.full_name} onChange={handleChange} className="w-full bg-[#f4f6f9] border border-transparent focus:border-[#006699] focus:bg-white focus:ring-0 rounded-none px-5 py-3.5 text-sm transition-all duration-300 outline-none" placeholder="John Doe" />
                    </div>
                    <div className="space-y-2">
                      <label className="text-xs font-bold text-[#00334d] uppercase tracking-widest flex items-center gap-2">
                        Email Address <span className="text-red-500">*</span>
                      </label>
                      <input type="email" name="email" required value={formData.email} onChange={handleChange} className="w-full bg-[#f4f6f9] border border-transparent focus:border-[#006699] focus:bg-white focus:ring-0 rounded-none px-5 py-3.5 text-sm transition-all duration-300 outline-none" placeholder="john@example.com" />
                    </div>
                    <div className="space-y-2">
                      <label className="text-xs font-bold text-[#00334d] uppercase tracking-widest flex items-center gap-2">
                        Phone Number <span className="text-red-500">*</span>
                      </label>
                      <input type="tel" name="phone" required value={formData.phone} onChange={handleChange} className="w-full bg-[#f4f6f9] border border-transparent focus:border-[#006699] focus:bg-white focus:ring-0 rounded-none px-5 py-3.5 text-sm transition-all duration-300 outline-none" placeholder="+216 55 555 555" />
                    </div>
                    <div className="space-y-2">
                      <label className="text-xs font-bold text-[#00334d] uppercase tracking-widest flex items-center gap-2">
                        Study Affiliation <span className="text-red-500">*</span>
                      </label>
                      <div className="relative">
                        <select name="affiliation" required value={formData.affiliation} onChange={handleChange} className="w-full bg-[#f4f6f9] border border-transparent focus:border-[#006699] focus:bg-white focus:ring-0 rounded-none px-5 py-3.5 text-sm transition-all duration-300 outline-none appearance-none cursor-pointer">
                          <option value="" disabled>Select your affiliation</option>
                          <option value="Preparatory student">Preparatory student</option>
                          <option value="Engineering">Engineering</option>
                          <option value="Bachelor-License">Bachelor-License</option>
                          <option value="Masters">Masters</option>
                        </select>
                        <div className="absolute inset-y-0 right-0 flex items-center pr-4 pointer-events-none">
                          <svg className="w-4 h-4 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" /></svg>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="w-full h-px bg-gradient-to-r from-transparent via-[#006699]/20 to-transparent my-8" />

                  <div className="space-y-8">
                    {/* Comments */}
                    <div className="space-y-2">
                      <label className="text-xs font-bold text-[#00334d] uppercase tracking-widest flex items-center justify-between">
                        <span>Any question or comment?</span>
                        <span className="text-[10px] text-gray-400 bg-gray-100 px-2 py-0.5">OPTIONAL</span>
                      </label>
                      <textarea name="comments" rows="4" value={formData.comments} onChange={handleChange} className="w-full bg-[#f4f6f9] border border-transparent focus:border-[#006699] focus:bg-white focus:ring-0 rounded-none px-5 py-4 text-sm transition-all duration-300 outline-none resize-none" placeholder="Write your message here..."></textarea>
                    </div>
                  </div>

                  <div className="pt-6">
                    <button type="submit" disabled={isSubmitting} className="w-full bg-[#006699] hover:bg-[#00334d] text-white font-black text-sm uppercase tracking-widest py-5 flex items-center justify-center gap-3 transition-all duration-300 rounded-none group disabled:opacity-70 disabled:cursor-not-allowed">
                      {isSubmitting ? (
                        <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                      ) : (
                        <>
                          <span>Submit Application</span>
                          <Send className="w-4 h-4 group-hover:translate-x-1 group-hover:-translate-y-1 transition-transform" />
                        </>
                      )}
                    </button>
                    <div className="mt-4 flex items-start gap-2 text-xs text-gray-500">
                      <AlertCircle className="w-4 h-4 text-[#006699] shrink-0" />
                      <p>By submitting this form, you agree to follow the IEEE Code of Ethics and actively participate in branch activities.</p>
                    </div>
                  </div>
                </form>
              </div>
            </motion.div>
          </div>

          {/* Right Sidebar: Other Units */}
          <div className="w-full lg:w-1/4">
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.2, duration: 0.5 }}
              className="bg-[#006699] p-6 text-white border border-[#004d73] shadow-xl relative"
            >
              {/* White L Corners for sidebar */}
              <div className="absolute top-0 left-0 w-4 h-4 border-t-2 border-l-2 border-white/50 pointer-events-none" />
              <div className="absolute bottom-0 right-0 w-4 h-4 border-b-2 border-r-2 border-white/50 pointer-events-none" />

              <h4 className="text-sm font-black uppercase tracking-widest mb-6 border-b border-white/20 pb-4">
                Other Units
              </h4>
              
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-1 gap-3">
                {otherUnits.map((other, index) => (
                  <motion.div
                    key={other.id}
                    initial={{ opacity: 0, x: 20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: 0.3 + index * 0.1, duration: 0.4 }}
                  >
                    <Link 
                      to={`/${other.id}register`}
                      className="group flex flex-col sm:flex-row items-center sm:items-start lg:items-center gap-2 sm:gap-4 bg-white/10 hover:bg-white/20 p-3 transition-all duration-300 border border-transparent hover:border-white/30 h-full text-center sm:text-left"
                    >
                      <div className="w-12 h-12 sm:w-16 sm:h-16 flex-shrink-0 flex items-center justify-center p-1 mx-auto sm:mx-0">
                        <img 
                          src={other.icon} 
                          alt={other.name} 
                          className="w-full h-full object-contain brightness-0 invert opacity-80 group-hover:opacity-100 transition-opacity" 
                        />
                      </div>
                      <div className="flex-1 w-full flex items-center justify-center sm:justify-start">
                        <span className="text-[10px] sm:text-xs font-bold uppercase tracking-wider block leading-snug break-words">
                          {other.name}
                        </span>
                      </div>
                      <ChevronRight className="hidden lg:block w-4 h-4 opacity-50 group-hover:opacity-100 group-hover:translate-x-1 transition-all" />
                    </Link>
                  </motion.div>
                ))}
              </div>

              <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.8, duration: 0.4 }}
                className="mt-8 pt-6 border-t border-white/20"
              >
                <Link
                  to="/subunits"
                  className="group flex items-center justify-center gap-2 w-full py-3.5 px-4 bg-white/10 hover:bg-white/20 text-white text-[10px] sm:text-xs font-bold uppercase tracking-widest transition-all duration-300 border border-white/20 hover:border-white/40 shadow-sm"
                >
                  <ArrowLeft className="w-4 h-4 opacity-70 group-hover:opacity-100 group-hover:-translate-x-1 transition-all" />
                  View All Units
                </Link>
              </motion.div>
            </motion.div>
          </div>

        </div>
      </main>
    </div>
  );
}
